/**
 * Role utility functions for workflow role compatibility.
 *
 * Note: page and feature authorization now comes from canonical permissions.
 * These helpers remain for workflow-level compatibility because
 * step.required_roles[] and station.required_roles[] still store the styling
 * role aliases operator/quality/site_manager.
 *
 * These utilities just provide consistent checking logic.
 */

import type { UserRole } from './roleColors';

/**
 * Check if user has at least one of the required roles.
 * Admins bypass all role checks.
 *
 * @param userRoles - Roles the user has at current site
 * @param requiredRoles - Roles required for the action (null/empty = no requirement)
 * @param isAdmin - Whether user is super admin
 * @returns true if user has access
 */
export function hasRequiredRole(
  userRoles: string[],
  requiredRoles: string[] | null | undefined,
  isAdmin: boolean = false
): boolean {
  // Admins bypass all checks
  if (isAdmin) return true;

  // No role requirement = everyone can access
  if (!requiredRoles || requiredRoles.length === 0) return true;

  // User must have at least one of the required roles
  return requiredRoles.some(role => userRoles.includes(role));
}

/**
 * Check if user can manage features (quality, site_manager, or admin).
 *
 * @param userRoles - Roles the user has at current site
 * @param isAdmin - Whether user is super admin
 * @returns true if user has manager-level access
 */
export function canManage(userRoles: string[], isAdmin: boolean = false): boolean {
  if (isAdmin) return true;
  return userRoles.some(role => role === 'site_manager' || role === 'quality');
}

export function hasManagerAccess(userRoles: string[], isAdmin: boolean = false): boolean {
  return isSiteManager(userRoles, isAdmin);
}

/**
 * Check if user is site manager (needed for user management, SAP import, etc).
 *
 * @param userRoles - Roles the user has at current site
 * @param isAdmin - Whether user is super admin
 * @returns true if user is site manager or admin
 */
export function isSiteManager(userRoles: string[], isAdmin: boolean = false): boolean {
  if (isAdmin) return true;
  return userRoles.includes('site_manager');
}

/**
 * Check if user can claim a specific station.
 *
 * @param userRoles - Roles the user has at current site
 * @param stationRequiredRoles - Roles required by the station
 * @param isAdmin - Whether user is super admin
 * @returns true if user can claim the station
 */
export function canClaimStation(
  userRoles: string[],
  stationRequiredRoles: string[] | null | undefined,
  isAdmin: boolean = false
): boolean {
  // Site managers and admins can claim any station
  if (isAdmin || userRoles.includes('site_manager')) return true;

  // Otherwise use standard role checking
  return hasRequiredRole(userRoles, stationRequiredRoles, isAdmin);
}
